import type { ImportComputation } from '@testflow/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api.js';

// #73: Abfrage-Intervall der Oberfläche, solange eine Berechnung auf dem Server läuft.
export const COMPUTATION_POLL_MS = 2000;
// #73 (Review M1): So lange (ab dem ersten Fehler der Serie) werden Netz- und 5xx-Fehler der Abfrage
// hingenommen. Ein Server-Neustart (Deploy) dauert länger als drei Abfragen; der Lauf selbst wird
// dabei zwar als abgebrochen markiert, aber das soll die Abfrage danach zeigen, nicht ein
// Netzfehler. Ein 4xx ist eine echte Antwort (Vorgang weg, kein Zugriff) und beendet sofort.
// Ein erfolgreicher Poll setzt zurück.
export const COMPUTATION_POLL_MAX_OUTAGE_MS = 60_000;

function isTransient(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status >= 500;
}

interface Options {
  start: () => Promise<ImportComputation>;
  poll: () => Promise<ImportComputation>;
  onDone: () => void;
}

// #73: Startet die Berechnung beim Mount (und bei `restart`) und fragt den Server ab, solange sie
// `running` ist. Die Berechnung selbst läuft im Hintergrund auf dem Server — hier hängt keine
// Anfrage mehr offen. `onDone` feuert genau einmal je Lauf. Callbacks laufen über Refs, damit ihr
// Identitätswechsel keinen Neustart auslöst.
export function useImportComputation(options: Options): {
  computation: ImportComputation | null;
  error: string | null;
  restart: () => void;
} {
  const [computation, setComputation] = useState<ImportComputation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `run` ist der gewollte Neustart-Auslöser.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setComputation(null);
    setError(null);
    let outageSince: number | null = null;

    const fail = (err: unknown): void => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'import_computation_failed');
    };
    const handle = (c: ImportComputation): void => {
      if (cancelled) return;
      outageSince = null;
      setComputation(c);
      // #73 (Review M2): Leerer Zustand nach einem Start heißt, jemand hat den Entwurf inzwischen
      // verworfen (z. B. eine Eingabe in einem anderen Tab). Es läuft nichts mehr, und es kommt
      // nichts — ohne diesen Zweig stünde die Oberfläche endlos auf „Lädt …“.
      if (c.state === null) {
        fail(new Error('import_computation_lost'));
      } else if (c.state === 'running') {
        timer = setTimeout(() => void tick(), COMPUTATION_POLL_MS);
      } else if (c.state === 'done') {
        optionsRef.current.onDone();
      }
    };
    const tick = async (): Promise<void> => {
      try {
        handle(await optionsRef.current.poll());
      } catch (err) {
        if (cancelled) return;
        const now = Date.now();
        outageSince ??= now;
        if (!isTransient(err) || now - outageSince >= COMPUTATION_POLL_MAX_OUTAGE_MS) fail(err);
        else timer = setTimeout(() => void tick(), COMPUTATION_POLL_MS);
      }
    };

    void (async () => {
      try {
        handle(await optionsRef.current.start());
      } catch (err) {
        fail(err);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [run]);

  const restart = useCallback(() => setRun((n) => n + 1), []);
  return { computation, error, restart };
}
